import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

/**
 * Unifica distintos formatos que vienen del backend (`start` ISO, epoch en ms/s,
 * moment de UI optimista). Evita que `moment(desconocido)` produzca "futuros"
 * y tiempos negativos cuando el servidor y el navegador no están alineados en TZ.
 */
export function parseStartMoment(raw) {
	if (raw == null || raw === '') return null;
	try {
		if (moment.isMoment(raw)) return raw.clone();
		if (typeof raw === 'number' && Number.isFinite(raw)) {
			// Heurística típica: unix en segundos vs milisegundos
			return raw > 1e12 ? moment(raw) : moment(raw * 1000);
		}
		if (typeof raw === 'object' && typeof raw.format === 'function') {
			return moment(raw);
		}
		const s = String(raw).trim();
		if (/^\d+$/.test(s)) {
			const n = Number(s);
			return n > 1e12 ? moment(n) : moment(n * 1000);
		}
		return moment(raw);
	} catch (_) {
		return null;
	}
}

/**
 * ElapsedTimer - Muestra el tiempo transcurrido desde una fecha de inicio
 * @param {string|object} startTime - Fecha/hora de inicio en formato ISO o objeto moment
 * @param {boolean} isRunning - Si el cronómetro debe estar activo
 * @param {object} style - Estilos para el componente
 * @param {number} maxTimeout - Tiempo máximo en segundos antes de detener el temporizador (opcional)
 * @param {function} onTimeout - Callback que se ejecuta cuando se alcanza el timeout (opcional)
 */
const ElapsedTimer = ({ startTime, isRunning = true, style = {}, maxTimeout = null, onTimeout = null }) => {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [hasTimedOut, setHasTimedOut] = useState(false);

  useEffect(() => {
    let interval = null;
    
    if (isRunning && startTime && !hasTimedOut) {
      const start = parseStartMoment(startTime);
      const now = moment();
      
      if (!start || !start.isValid()) {
        console.warn('ElapsedTimer: startTime no es una fecha válida:', startTime);
        return;
      }
      
      // Si start está en el futuro (reloj servidor vs cliente, ISO sin TZ, etc.)
      // no mostrar tiempos negativos raros: mostrar 0 hasta que alcance tiempo real.
      const rawSeconds = moment.duration(now.diff(start)).asSeconds();
      const initialElapsed = Number.isFinite(rawSeconds) ? Math.max(0, rawSeconds) : 0;
      setElapsedTime(initialElapsed);
      
      // Actualizar cada segundo
      interval = setInterval(() => {
        const now = moment();
        const raw = moment.duration(now.diff(start)).asSeconds();
        const elapsed = Number.isFinite(raw) ? Math.max(0, raw) : 0;
        
        // Verificar timeout máximo
        if (maxTimeout && elapsed >= maxTimeout) {
          setHasTimedOut(true);
          
          // Ejecutar callback de timeout si está definido (solo una vez)
          if (onTimeout && typeof onTimeout === 'function' && !hasTimedOut) {
            onTimeout();
          }
          
          // NO detener el temporizador, solo marcar que se alcanzó el timeout
          // El temporizador continuará funcionando indefinidamente
        }
        
        setElapsedTime(elapsed);
      }, 1000);
    }
    
    // Limpiar el intervalo cuando el componente se desmonte
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [startTime, isRunning, maxTimeout, hasTimedOut]);
  
  // Resetear timeout cuando cambia startTime o isRunning
  useEffect(() => {
    setHasTimedOut(false);
  }, [startTime, isRunning]);
  
  // Formatear el tiempo transcurrido en días, horas, minutos y segundos
  const formatElapsedTime = (seconds) => {
    const s = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
    const days = Math.floor(s / 86400);
    const hours = Math.floor((s % 86400) / 3600);
    const minutes = Math.floor((s % 3600) / 60);
    const remainingSeconds = s % 60;
    const paddedSeconds = remainingSeconds.toString().padStart(2, '0');

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m ${paddedSeconds}s`;
    }
    if (hours > 0) {
      return `${hours}h ${minutes}m ${paddedSeconds}s`;
    }
    return `${minutes}m ${paddedSeconds}s`;
  };

  // No mostrar "0m 00s" congelado si start no parsea (API rara / TZ); coherente con el effect.
  const parsedDisplay = startTime ? parseStartMoment(startTime) : null;
  if (!isRunning || !parsedDisplay || !parsedDisplay.isValid()) {
    return null;
  }
  
  return (
    <span className="elapsed-time" style={{ fontSize: '0.85em', opacity: 0.85, ...style }}>
      ({formatElapsedTime(elapsedTime)})
      {hasTimedOut && <span style={{ color: '#ff6b6b', marginLeft: '5px' }}>⏰</span>}
    </span>
  );
};

ElapsedTimer.propTypes = {
  startTime: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object // Para objetos moment
  ]), // Fecha/hora de inicio en formato ISO
  isRunning: PropTypes.bool,  // Si el cronómetro debe estar activo
  style: PropTypes.object,    // Estilos adicionales
  maxTimeout: PropTypes.number, // Tiempo máximo en segundos (opcional)
  onTimeout: PropTypes.func    // Callback que se ejecuta cuando se alcanza el timeout (opcional)
};

export default ElapsedTimer;